import {
  ActiveQuarter,
  ActiveQuarterWrapper,
  ChangeQuarterLink,
  GlobalStyle,
  MapKeyWrapper,
  MapWrapper,
  NavWrapper,
  PageWrapper,
  Wrapper,
} from "./App.styles";

import Heading from "./components/Heading";
import Map from "./components/Map";
import MapKey from "./components/MapKey";
import Nav from "./components/Nav";
import NavItem from "./components/NavItem";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";

import {
  Switch,
  Route,
  useRouteMatch,
  useHistory,
  useLocation,
} from "react-router-dom";

import Headlines from "./pages/Headlines";
import Home from "./pages/Home";
import { useMedia } from "react-use";
import { device } from "./device";
import Archive from "./pages/Archive";
import Error from "./pages/Error";
import About from "./pages/About";
import { convertToSlug } from "./utils";

function getCurrentYearData(activeQuarter, years) {
  const [year, quarter] = activeQuarter;

  if (!years[year]) {
    return false;
  }

  return years[year][quarter];
}
function ucfirst(string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}
function App() {
  // Don't use from react-router, as we need this right away.
  // const location = useLocation();

  const { t } = useTranslation();

  const history = useHistory();

  const [data, setData] = useState(null);
  const [activeLocation, setActiveLocation] = useState(null);
  const isLargerDevice = useMedia(device.tablet);
  const [activeQuarter, setActiveQuarter] = useState(null);
  const location = useLocation();
  const mapWrapperRef = useRef(null);
  const pageWrapperRef = useRef(null);

  const isHomepage = useRouteMatch({
    path: "/",
    exact: true,
  });

  // Handle loading data.
  // We'll load all quarters here as it's only 8KB uncompressed per quarter!
  // If this gets unmanagable - it can be split and a dependency can be added here.
  useEffect(() => {
    const dataVersion = window.__HPI_DATA_VERSION
      ? window.__HPI_DATA_VERSION
      : "";
    fetch(process.env.PUBLIC_URL + "/new-data.json?v=" + dataVersion)
      .then((res) => res.json())
      .then((res) => {
        setData(res);
      });
  }, []);

  useEffect(() => {
    let friendly_name = location.pathname.replaceAll("/", "");
    if (location.pathname === "/") {
      friendly_name = "Home";
    }
    friendly_name = ucfirst(friendly_name);
    // eslint-disable-next-line no-restricted-globals
    parent.postMessage(`event~HousePriceIndex~Map~${friendly_name}~0`, "*");
  }, [location.pathname]);

  // Google analytics.
  useEffect(() => {
    if (!data) return;

    let quarter = activeQuarter ?? data.active_quarter;

    let regions = getCurrentYearData(quarter, data.years);

    if (regions && activeLocation !== null) {
      let { title } = regions.locations[activeLocation];
      // eslint-disable-next-line no-restricted-globals
      parent.postMessage(`event~HousePriceIndex~Map~${title}~0`, "*");
    }
  }, [activeQuarter, activeLocation, data]);

  // Passed down as props, this is sets the current active location
  // Used by both the Map and the MapList/MapDetail
  const handleToggleActiveLocation = (id, state) => {
    setActiveLocation(state && id !== activeLocation ? id : null);
  };

  /**
   * Passed into the Archive component to set the quarter
   * @param {*} year
   * @param {*} quarter
   */
  const handleChangeQuarter = (year, quarter) => {
    setActiveQuarter([year, quarter]);
    history.push("/");
  };

  /**
   * Handle ESC
   */
  const handleReset = (event) => {
    if (event.keyCode === 27) {
      setActiveLocation(null);
      return;
    }
    // Left
    // if (event.keyCode === 37 && mapWrapperRef.current) {
    //   mapWrapperRef.current.focus();
    //   return;
    // }

    if (event.keyCode === 39 && mapWrapperRef.current) {
      pageWrapperRef.current.focus();
    }
  };

  // useEffect(() => {
  //   if (!isHomepage) {
  //     setActiveLocation(null);
  //     history.push("/");
  //     // setActiveLocation(null);
  //   }
  // }, [isHomepage, history, activeLocation]);

  // Scroll the MapDetail into view when selected.
  useEffect(() => {
    if (activeLocation !== null && isHomepage) {
      // short circuit
      if (!isHomepage) {
        setActiveLocation(null);
        history.push("/");
        return;
      }

      const element = document.querySelector(`*[data-id="${activeLocation}"]`);

      // TOOD: look into why smoothscroll doesnt work on smaller devices while inside iframe
      if (isLargerDevice) {
        element.focus();
        element.scrollIntoView({ behavior: "smooth", block: "nearest" });
      } else {
        element.focus();
        element.scrollIntoView();
      }
    }
  }, [activeLocation, isLargerDevice, isHomepage, history]);

  const menuItems = [
    { title: t("nav.housePriceIndex"), link: "/", exact: true },
    { title: t("nav.headlines"), link: "/headlines" },
    { title: t("nav.archive"), link: "/archive" },
    { title: t("nav.about"), link: "/about" },
  ];

  // Wait for data to be loaded.
  if (!data) {
    return <div>{t("app.loading")}</div>;
  }

  const quarter = activeQuarter ?? data.active_quarter;
  const quarterData = getCurrentYearData(quarter, data.years);

  // Handle errors.
  if (!quarterData || !quarterData.locations.length) {
    return (
      <>
        <GlobalStyle />
        <Wrapper fullWidth>
          <Heading>{t("app.heading")} </Heading>
          <Error message={t("app.error.missing")} />
        </Wrapper>
      </>
    );
  }

  return (
    <>
      <GlobalStyle />
      <Wrapper data-testid="wrapper" onKeyUp={handleReset}>
        <ActiveQuarterWrapper>
          <ActiveQuarter>
            {t("app.quarterAbbr")}
            {quarter[1]} {quarter[0]} -{" "}
          </ActiveQuarter>
          <ChangeQuarterLink to="/archive">
            {t("app.changeButtonLinkText")}
          </ChangeQuarterLink>
        </ActiveQuarterWrapper>

        <NavWrapper>
          <Nav>
            {menuItems.map((item) => (
              <NavItem
                key={item.link}
                to={item.link}
                exact={item.exact ?? null}
              >
                {item.title}
              </NavItem>
            ))}
          </Nav>
        </NavWrapper>
        {/* When we're on a mobile device and on a different page, let's hide the map for UX reasons */}
        {(isLargerDevice || isHomepage) && (
          <>
            <MapKeyWrapper>
              <MapKey averageHousePrice={quarterData.headline.average_price} />
            </MapKeyWrapper>

            <MapWrapper tabIndex="-1" ref={mapWrapperRef}>
              <Map
                locations={quarterData.locations}
                activeLocation={quarterData.locations[activeLocation]}
                onLocationChange={handleToggleActiveLocation}
              />
            </MapWrapper>
          </>
        )}

        <PageWrapper ref={pageWrapperRef} tabIndex="-1">
          <Switch>
            <Route path="/headlines">
              <Headlines figures={quarterData.headline} />
            </Route>
            <Route path="/archive">
              <Archive
                activeQuarter={quarter}
                quarters={data.sorted_quarters}
                onQuarterChange={handleChangeQuarter}
              />
            </Route>

            <Route path="/about">
              <About />
            </Route>

            <Route path="/" exact>
              <Home
                locations={quarterData.locations}
                onLocationChange={handleToggleActiveLocation}
                activeLocationIndex={activeLocation}
              />
            </Route>
          </Switch>
        </PageWrapper>
      </Wrapper>
    </>
  );
}

export default App;
